import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { RootState, AppDispatch } from 'app/store';
import {
  initializeAbp,
  refreshAbpPermissions,
  resetAbpError,
  forceReinitialize
} from 'slices/abp/abpSlice';
import { useMemo, useCallback, useEffect, useState } from 'react';
import { actorsAPI } from 'services/swagger-api';
import { RoleNames } from 'core/roles';
import { GetConnectedUserInfosResponse } from 'api/actor';

// Single-flight partagé entre toutes les instances du hook : useAbp est monté
// par chaque PermissionGuard/menu simultanément (4-6 instances), et chaque
// renouvellement silencieux OIDC re-déclenchait autant d'appels parallèles à
// getConnectedUserInfos. On cache la promesse par access_token : même session,
// même réponse ; un nouveau token (login, switch, impersonation) invalide le cache.
let userInfosCache: { token: string; promise: Promise<GetConnectedUserInfosResponse> } | null = null;

const getConnectedUserInfosSingleFlight = (token: string): Promise<GetConnectedUserInfosResponse> => {
  if (!userInfosCache || userInfosCache.token !== token) {
    const promise = actorsAPI.getConnectedUserInfos().catch((err) => {
      // Ne pas mettre l'échec en cache : la prochaine instance retentera.
      if (userInfosCache?.promise === promise) userInfosCache = null;
      throw err;
    });
    userInfosCache = { token, promise };
  }
  return userInfosCache.promise;
};

export const useAbp = () => {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useAuth();
  const [connectedUserInfos, setConnectedUserInfos] = useState<GetConnectedUserInfosResponse>();
  const [loadingUserInfos, setLoadingUserInfos] = useState(false);

  const { configuration, initialized, loading, error } = useSelector(
    (state: RootState) => state.abp
  );

  // grantedPolicies = permissions ACCORDÉES au rôle actif (le back scope les claims
  // au rôle actif). auth.policies est le catalogue des permissions existantes —
  // inutilisable en gating (toujours true).
  const permissions = useMemo(() => configuration?.auth?.grantedPolicies ?? {}, [configuration]);

  useEffect(() => {
    let cancelled = false;
    const getConnectedUserInfos = async () => {
      const token = auth.user?.access_token;
      if (!auth.isAuthenticated || !token) {
        setLoadingUserInfos(false);
        return;
      }

      setLoadingUserInfos(true);
      try {
        const response = await getConnectedUserInfosSingleFlight(token);
        if (!cancelled) setConnectedUserInfos(response);
      } catch (error) {
        console.error('Erreur lors de la récupération des infos utilisateur:', error);
      } finally {
        if (!cancelled) setLoadingUserInfos(false);
      }
    };
    getConnectedUserInfos();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.user]);

  const initialize = useCallback(() => {
    dispatch(resetAbpError());
    dispatch(initializeAbp());
  }, [dispatch]);

  // Synchroniser l'état d'authentification OIDC avec ABP
  useEffect(() => {
    if (auth.isAuthenticated && !initialized && !loading && !error) {
      initialize();
    }
  }, [auth.isAuthenticated, initialized, loading, error, initialize]);

  const hasPermission = useCallback((policy: string): boolean => {
    if (!policy) return true;
    const hasAccess = permissions[policy] === true;
    return hasAccess;
  }, [permissions]);

  const hasAnyPermission = useCallback((policies: readonly string[] | string[]): boolean => {
    if (!policies.length) return true;
    const hasAccess = policies.some(policy => hasPermission(policy));
    return hasAccess;
  }, [hasPermission]);

  const hasAllPermissions = useCallback((policies: readonly string[] | string[]): boolean => {
    if (!policies.length) return true;
    const hasAccess = policies.every(policy => hasPermission(policy));
    return hasAccess;
  }, [hasPermission]);

  const hasRole = useCallback((role: string): boolean => {
    const hasRole = connectedUserInfos?.roles?.includes(role) ?? false;
    return hasRole;
  }, [connectedUserInfos]);

  const isAdmin = useMemo((): boolean => {
    // Rôles ABP du token (≠ rôle actif) : littéral centralisé dans RoleNames.
    return connectedUserInfos?.roles?.includes(RoleNames.Admin) ?? false;
  }, [connectedUserInfos]);


  const refresh = useCallback(() => {
    dispatch(resetAbpError());
    dispatch(refreshAbpPermissions());
  }, [dispatch]);

  const forceRestart = useCallback(async () => {
    await dispatch(forceReinitialize());
    dispatch(initializeAbp());
  }, [dispatch]);

  const logout = useCallback(() => {
    // Clear ABP state
    dispatch(forceReinitialize());
    // Trigger OIDC logout
    auth.signoutRedirect();
  }, [dispatch, auth]);

  return {
    // État
    connectedUserInfos,
    permissions,
    initialized,
    loading,
    error,
    isAdmin,
    isAuthenticated: auth.isAuthenticated,

    // Méthodes de vérification
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,

    loadingUserInfos,

    // Actions
    initialize,
    refresh,
    forceRestart,
    logout
  };
};
